import { useMemo } from 'react';

import { MeasurementSeriesStatusMap, StructuredMeasurements, WindowSize } from '@restDataAccess';
import { FlexAligner, Tooltip } from '@scaleaq/scaleui';
import { InventoryProvider, MappingCollections, MeasurementRange } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { EllipsisTypography } from '../../styles.ts';
import { DataRow, DataRowDivider } from '../dataRow';
import { liceColors } from './colorLookup.ts';
import { getLatestRangeMean, getWindowSizeKey } from './liceCountWidget.functions.ts';
import { AdultFemaleRow } from './liceCountWidgetContentWithData/AdultFemaleRow.tsx';

type LiceCountWidgetContentWithDataProps = {
  id: string;
  windowSize: WindowSize;
  /**
   * Shared measurement ranges from the widget's single `useDataFromConfig` fetch (same payload
   * as the chart). Latest-day means are derived here so the rows do not issue their own requests.
   */
  allMeasurements: StructuredMeasurements<MeasurementRange>;
  /**
   * Per plot-type / sensor status from that same fetch. Use this for row loading/error instead of
   * the aggregate flags (those only cover "every series failed" / "no series loaded yet").
   */
  seriesStatus: MeasurementSeriesStatusMap;
};

/**
 * Lice count summary rows fed by parent-owned chart data.
 * Replaces `LiceCountWidgetContent` (three `useLiceCount` calls) when the widget already loads
 * female / mobile / caligus ranges for the chart.
 */
export const LiceCountWidgetContentWithData = ({
  windowSize,
  id,
  allMeasurements,
  seriesStatus,
}: LiceCountWidgetContentWithDataProps) => {
  const { t } = useTranslation(['orbitOne']);
  const windowSizeKey = useMemo(() => getWindowSizeKey(windowSize), [windowSize]);

  const adultFemaleKey =
    MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemale];
  const adultFemaleEggStringKey =
    MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemaleEggString];
  const adultFemaleNoEggStringKey =
    MappingCollections.inventoryProviderClassPair[
      InventoryProvider.LiceCountAdultFemaleNoEggString
    ];
  const mobileKey =
    MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountMobile];
  const caligusKey =
    MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountCaligus];

  const female = getLatestRangeMean(allMeasurements[adultFemaleKey]?.[id]);
  const femaleEggString = getLatestRangeMean(allMeasurements[adultFemaleEggStringKey]?.[id]);
  const femaleNoEggString = getLatestRangeMean(allMeasurements[adultFemaleNoEggStringKey]?.[id]);
  const mobile = getLatestRangeMean(allMeasurements[mobileKey]?.[id]);
  const caligus = getLatestRangeMean(allMeasurements[caligusKey]?.[id]);

  const femaleStatus = seriesStatus[adultFemaleKey]?.[id];
  const femaleEggStringStatus = seriesStatus[adultFemaleEggStringKey]?.[id];
  const femaleNoEggStringStatus = seriesStatus[adultFemaleNoEggStringKey]?.[id];
  const mobileStatus = seriesStatus[mobileKey]?.[id];
  const caligusStatus = seriesStatus[caligusKey]?.[id];

  return (
    <FlexAligner direction={'column'}>
      <Tooltip tooltip={t(`orbitOne:liceCount.${windowSizeKey}`)} placement='top'>
        <EllipsisTypography type='body_large' style={{ width: '100%' }}>
          {t(`orbitOne:liceCount.${windowSizeKey}`)}
        </EllipsisTypography>
      </Tooltip>
      <AdultFemaleRow
        female={{
          label: t('orbitOne:liceCount.rowTitles.adultFemale'),
          value: female,
          status: femaleStatus,
          color: liceColors[adultFemaleKey],
        }}
        breakdown={[
          {
            label: t('orbitOne:liceCount.rowTitles.adultFemaleEggString'),
            value: femaleEggString,
            status: femaleEggStringStatus,
            color: liceColors[adultFemaleEggStringKey],
          },
          {
            label: t('orbitOne:liceCount.rowTitles.adultFemaleNoEggString'),
            value: femaleNoEggString,
            status: femaleNoEggStringStatus,
            color: liceColors[adultFemaleNoEggStringKey],
          },
        ]}
      />
      <DataRowDivider />
      <DataRow
        label={t('orbitOne:liceCount.rowTitles.mobile')}
        value={mobile}
        isError={mobileStatus?.isError ?? false}
        isLoading={mobileStatus?.isLoading ?? false}
        color={liceColors[mobileKey]}
      />
      <DataRowDivider />
      <DataRow
        label={t('orbitOne:liceCount.rowTitles.caligus')}
        value={caligus}
        isError={caligusStatus?.isError ?? false}
        isLoading={caligusStatus?.isLoading ?? false}
        color={liceColors[caligusKey]}
      />
    </FlexAligner>
  );
};
